import React, { ReactNode, type JSX } from 'react';
import moment from 'moment-timezone';
import { useTypedSelector } from '@fiji/common';
import { Grid, Stack, Typography } from '@mui/material';
import { CurrentDateTimeIndicator, DayViewHeader, DeviceTypeRow, DeviceRow } from './';
import { CustomEmptyState } from 'components/CustomEmptyState';
import { Schedule } from '@mui/icons-material';
import { CustomTransComponent } from 'components/CustomTransComponent';
import { useTransformData } from 'hooks/useTransformData';

export const DayView = React.memo(
    ({ gridLabel, leftGrid }: { gridLabel: ReactNode; leftGrid?: JSX.Element }): JSX.Element => {
        const schedules = useTypedSelector((state) => state['schedule']['schedules']);
        const selectedDate = useTypedSelector((state: any) => state['schedule']['selectedDate']);
        const isGroupingEnabled = useTypedSelector((state) => state['schedule']['isGroupingEnabled']);
        const { timezone: selectedTimezone } = useTransformData();

        const getDayViewContent = (): any => {
            if (!schedules?.length) {
                return (
                    <CustomEmptyState
                        sx={{ position: 'relative', left: '-90px' }}
                        icon={<Schedule fontSize={'inherit'} />}
                        title={<CustomTransComponent translationKey={'SCHEDULING:NO_SCHEDULE_ACTIONS'} />}
                        description={<CustomTransComponent translationKey={'SCHEDULING:ADD_SCHEDULE_ACTIONS'} />}
                    />
                );
            } else if (isGroupingEnabled) {
                return <DeviceTypeRow />;
            }
            return <DeviceRow />;
        };

        // Timezone-aware date comparison
        const currentDateInTz = moment().tz(selectedTimezone).format('YYYY-MM-DD');
        const selectedDateInTz = moment(selectedDate).format('YYYY-MM-DD');

        return (
            <div style={{ position: 'relative', paddingTop: 72, overflow: 'auto' }} className="margin-top-36">
                <Grid container>
                    <Grid size={2}>
                        <Stack
                            style={{
                                height: 10,
                                display: 'flex',
                                justifyContent: 'center',
                                margin: 0,
                            }}
                            className="border-right-1 border-bottom-1 padding-left-16"
                        >
                            <Typography variant="body2" style={{ fontWeight: 'bold' }} pb={'20px'}>
                                {gridLabel ?? 'Device'}
                            </Typography>
                        </Stack>
                        {leftGrid}
                    </Grid>
                    <Grid size={10}>
                        <Stack direction="row">
                            {currentDateInTz === selectedDateInTz && <CurrentDateTimeIndicator />}
                            <Stack width="100%">
                                <DayViewHeader />
                                {getDayViewContent()}
                            </Stack>
                        </Stack>
                    </Grid>
                </Grid>
            </div>
        );
    }
);
